import React, { useState, useRef, useEffect } from 'react';
import { ToggleSwitch, Slider } from './SettingsElements';
import { ColorScheme, useColors } from '../theme/colorThemeContext';
import { PanelId } from '../panels/registry';

/** What Settings needs in order to show and change the panel stack. */
export interface PanelControls {
    /** Switched on — in the stack, whether or not it is scrolled into view. */
    isOn: (id: PanelId) => boolean;
    /** Switches the panel off, or on and into view. Never refused. */
    toggle: (id: PanelId) => void;
    /** Move the panel to that place in the stack the meter draws. */
    moveTo: (id: PanelId, index: number) => void;
    /** The whole stack, left to right, with this panel included wherever it sits. */
    meter: (id: PanelId) => {
        segments: { id: PanelId; fraction: number; self: boolean; inView: boolean }[];
        gapFraction: number;
    };
}

interface SettingsProps {
    onSettingChange: (setting: string, value: boolean | string | number) => void;
    onOpenSentimentCustomizer: () => void;
    panels: PanelControls;
    settings: {
        preferNotationLegible: boolean;
        showScaleDegrees: boolean;
        // showMidiPlayer: boolean; // MIDI Player always shown, not turned off with settings.showMidiPlayer
        showIntervalLabels: boolean;
        showPieSentiments: boolean;
        showOvertones: boolean;
        showNashvilleNumber: boolean;
        showTonicScale: boolean;
        showRelativeChordScale: boolean;
        showChordScale: boolean;
        preferModeBorrowing: boolean;
        preferWhiteStaffPaper: boolean;
        showKeyCenterRegions: boolean;
        showChordQualityColors: boolean;
        secondaryChordThreshold: number;
        showRootFifthSentiment: boolean;
        anchorToChordRoot: boolean;
        chartMidiOnLoad: boolean;
        // showSelectedSentiments: boolean; // Possible to implement in future, if the chord chart selected root is preferred
        appTheme: string;
        // Add more settings here as needed
    };
}

type ColorSchemeKeys = keyof Omit<ReturnType<typeof useColors>, 'setColorScheme' | 'updateColors' | 'transformPalette'>;

type ColorPaletteOption = {
    label: string;
    value: ColorSchemeKeys;
};

const colorPaletteOptions: ColorPaletteOption[] = [
    { label: "Soft", value: "softColors" },
    { label: "Soft Major/Minor", value: "softMajMinColors" },
    { label: "Spectrum", value: "spectrumColors" },
    { label: "Major/Minor", value: "majMinColors" },
    // { label: "Major", value: "majorColors" },
    // { label: "Minor", value: "minorColors" },
    { label: "Emotion Groups", value: "emoGroupsColors" },
    // { label: "Emotion Groups Minor", value: "emoGroupsMinColors" },
    { label: "Chord Quality - Soft", value: "followModeSoftColors" },
    { label: "Chord Quality - Harsh", value: "followModeHarshColors" },
    { label: "Valence - Bright", value: "valenceBrightColors" },
    // { label: "Valence - Dark", value: "valenceDarkColors" },
    { label: "Extensions", value: "extensionsColors" },
    { label: "Uniform Teal", value: "uniformTealColors" },
    { label: "Uniform Orange", value: "uniformOrangeColors" },
    { label: "Uniform White", value: "uniformWhiteColors" },
    { label: "Hard", value: "hardColors" },
    { label: "Syn - Taylor Eigsti", value: "synTEigstiColors" },
    { label: "Syn - Hadrien Feraud", value: "synHFeraudColors" },
    { label: "Custom Palette 1", value: "customColors1" },
];

// Add this new component before the Settings component
interface ColorPickerPopupProps {
    currentColor: string;
    softMajMinColor: string;
    onColorChange: (newColor: string) => void;
    onClose: () => void;
    position: { x: number; y: number };
    currentNote: string;
    isMajor: boolean;
    customPalette: ColorScheme;
}

const rgbToHex = (color: string): string => {
    // If already in hex format, return as is
    if (color.startsWith('#')) return color;

    // Extract RGB values
    const matches = color.match(/\d+/g);
    if (!matches || matches.length < 3) return '#000000';

    // Convert to hex
    const r = parseInt(matches[0]);
    const g = parseInt(matches[1]);
    const b = parseInt(matches[2]);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const majorNotes = ['R', '♭2', '2', '♭3', '3', '4', '♯4', '5', '♭6', '6', '♭7', '7'];
const minorNotes = ['R', '♭2', '2', '♭3', '3', '4', '♭5', '5', '♭6', '6', '♭7', '7'];

const ColorPickerPopup: React.FC<ColorPickerPopupProps> = ({
    currentColor,
    onColorChange,
    onClose,
    position,
    currentNote,
    isMajor,
    customPalette
}) => {
    // const [initialColor] = useState(currentColor);
    const [newColor, setNewColor] = useState(rgbToHex(currentColor));
    const colorInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);


    useEffect(() => {
        const timer = setTimeout(() => {
            if (colorInputRef.current) {
                colorInputRef.current.click();
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [currentColor]); // Re-run when currentColor changes

    useEffect(() => {
        if (colorInputRef.current) {
            const hexColor = rgbToHex(currentColor);
            colorInputRef.current.value = hexColor;
            setNewColor(hexColor);
        }
    }, [currentColor]);

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const hexColor = rgbToHex(e.target.value);
        setNewColor(hexColor);
        // onColorChange(hexColor); // Convert to hex before passing up
    };

    const handleClose = () => {
        onColorChange(newColor); // Only update the parent's color when closing
        onClose();
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'fixed',
                top: position.y,
                left: position.x,
                backgroundColor: '#1a1a1a',
                padding: '1.5dvw',
                borderRadius: '0.7dvw',
                border: 'groove',
                borderColor: '#555',
                zIndex: 1003,
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                minWidth: '20dvw',
                minHeight: '10dvw',
                // maxHeight: '20dvw',
                // overflowY: 'auto',
                transform: 'translateX(-50%)', // Center horizontally relative to click position
            }}>

            <div style={{
                display: 'flex',
                // gap: '1dvw',
                marginBottom: '1dvw'
            }}>
                <div>
                    <div style={{ color: '#999', fontSize: '0.8dvw', marginBottom: '0.3dvw', textAlign: 'center' }}>Old</div>
                    <div style={{
                        width: '3dvw',
                        height: '3dvw',
                        backgroundColor: currentColor,
                        borderRadius: '0.3dvw 0 0 0.3dvw',
                        // border: '1px solid #555',
                        cursor: 'pointer'
                    }}
                        onClick={() => {
                            setNewColor(rgbToHex(currentColor)); // Allow clicking Current to reset New to initial color
                        }}
                    />
                </div>
                <div>
                    <div style={{ color: '#999', fontSize: '0.8dvw', marginBottom: '0.3dvw', textAlign: 'center' }}>New</div>
                    <input
                        ref={colorInputRef}
                        type="color"
                        value={rgbToHex(newColor)}
                        onChange={handleColorChange}
                        onClick={(e) => e.currentTarget.click()}
                        style={{
                            width: '3dvw',
                            height: '3dvw',
                            padding: 0,
                            border: 'none',
                            borderWidth: '0',
                            borderRadius: '0 0.3dvw 0.3dvw 0',
                            cursor: 'pointer',
                            backgroundColor: newColor,
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            outline: 'none',
                            boxShadow: 'none'
                        }}
                    />
                </div>
            </div>

            {/* Title and close button in separate div below */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1dvw'
            }}>
                <span style={{ fontSize: '1dvw', color: '#ddd', fontFamily: 'Arial Unicode MS, Bravura Text' }}>
                    {isMajor ? 'Major' : 'Minor'} {isMajor ? majorNotes[Object.keys(customPalette).indexOf(currentNote)] : minorNotes[Object.keys(customPalette).indexOf(currentNote)]}
                </span>
                <div style={{ display: 'flex', gap: '0.3dvw' }}>
                    <span
                        onClick={() => {
                            onClose(); // Just close without saving
                        }}
                        style={{
                            cursor: 'pointer',
                            fontSize: '0.9dvw',
                            padding: '0.3dvw 0.3dvw',
                            borderRadius: '0.3dvw',
                            backgroundColor: '#333'
                        }}
                    >
                        Cancel ✕
                    </span>
                    <span
                        onClick={() => {
                            handleClose(); // Save and close
                        }}
                        style={{
                            cursor: 'pointer',
                            fontSize: '0.9dvw',
                            padding: '0.3dvw 0.3dvw',
                            borderRadius: '0.3dvw',
                            backgroundColor: 'rgb(75, 149, 149)',
                            color: '#fff'
                        }}
                    >
                        Apply ✓
                    </span>
                </div>
            </div>
        </div>
    );
};

// A miniature of the panel stack, and the control that turns a panel on and off.
//
// The track is the whole stack, left to right, and each block is a panel drawn
// to scale against the others. The blocks the view reaches are drawn solid and
// the rest of the stack faint, so the meter says where this panel sits and
// whether it is on screen. The panel the box belongs to is the coloured block. When the panel is off, it is drawn
// faintly in the slot it would return to — the meter previews what the toggle
// will do before it is clicked, which a switch could not.
//
// The blocks are also the controls, and which block you click says what you
// mean: this panel's own block turns it on and off, and any OTHER block moves
// this panel to that place in the stack — the meter is a map, so clicking where
// you want the panel to be is the obvious gesture. That is the same rearranging
// the move arrows under each panel do, but over the whole stack at once and
// without having to have the panel on screen first.
//
// For a panel that is OFF, a block says "put it here", and putting it somewhere
// means showing it — one click, not a click to place it and a second on the
// block it just landed on to bring it back.
const PanelStackMeter: React.FC<{
    segments: { id: string; fraction: number; self: boolean; inView: boolean }[];
    gapFraction: number;
    /** Switched on — in the stack, whether or not it is on screen (that is `inView`). */
    isOn: boolean;
    colorOn: string;
    label: string;
    onSelf: () => void;
    onMoveTo: (index: number) => void;
}> = ({ segments, gapFraction, isOn, colorOn, label, onSelf, onMoveTo }) => {
    const blockColor = (seg: { self: boolean; inView: boolean }) => {
        if (seg.self) {
            return colorOn;
        }
        return seg.inView ? 'rgba(255, 255, 255, 0.32)' : 'rgba(255, 255, 255, 0.12)';
    };

    // An off panel is faint; an on panel the view does not reach is between.
    const blockOpacity = (seg: { self: boolean; inView: boolean }) => {
        if (!seg.self || seg.inView) {
            return 1;
        }
        return isOn ? 0.6 : 0.45;
    };

    const blockHint = (self: boolean) => {
        if (self) {
            return isOn ? `Hide ${label}` : `Show ${label}`;
        }
        return isOn ? `Move ${label} here` : `Show ${label} here`;
    };

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35dvw', verticalAlign: 'middle' }}>
            <span
                style={{
                    display: 'inline-flex',
                    gap: `${gapFraction * 100}%`,
                    width: '4dvw',
                    height: '1.05dvw',
                    borderRadius: '0.12dvw',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.1)'
                }}
            >
                {segments.map((seg, index) => (
                    <span
                        key={seg.id}
                        className="settings-meter-block"
                        title={blockHint(seg.self)}
                        // The header behind this already toggles the panel, so a
                        // block that means something else has to stop the click.
                        onClick={(e) => {
                            e.stopPropagation();
                            if (seg.self) {
                                onSelf();
                            } else {
                                onMoveTo(index);
                            }
                        }}
                        style={{
                            flex: `0 0 ${seg.fraction * 100}%`,
                            background: blockColor(seg),
                            borderRadius: '0.1dvw',
                            opacity: blockOpacity(seg)
                        }}
                    />
                ))}
            </span>
        </span>
    );
};

const Settings: React.FC<SettingsProps> = ({ onSettingChange, onOpenSentimentCustomizer, settings, panels }) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [hoveredOption, setHoveredOption] = useState<string | null>(null);
    const { setColorScheme, ...colorSchemes } = useColors();
    const [selectedPalette, setSelectedPalette] = useState("softMajMinColors"); // Default value
    const [isPaletteSelectOpen, setIsPaletteSelectOpen] = useState(false);

    // Helper to get colorOn/colorOff based on theme
    const getColorOn = () => settings.appTheme === 'dark' ? 'rgb(69, 207, 207)' : settings.appTheme === 'light' ? 'rgb(0, 255, 255)' : 'rgb(69, 207, 207)';
    const getColorOff = () => settings.appTheme === 'dark' ? 'rgb(120, 120, 120)' : settings.appTheme === 'light' ? 'rgb(215, 215, 215)' : 'rgb(120, 120, 120)';

    // The panel boxes share a header: name, stack meter, on/off dot.
    const panelBoxGlow = (id: PanelId) => ({
        outlineColor: panels.isOn(id) ? getColorOn() : getColorOff(),
        boxShadow: `0 0 0.4dvw 0.1dvw ${panels.isOn(id) ? getColorOn() : 'rgba(0, 0, 0, 0)'}`
    });

    const panelHeader = (id: PanelId, label: string) => {
        const isOn = panels.isOn(id);
        const bar = panels.meter(id);

        return (
            <div
                onClick={() => panels.toggle(id)}
                title={isOn ? `Hide ${label}` : `Show ${label}`}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                }}
            >
                <div
                    className={`settings-panel-title ${isOn ? 'on' : 'off'}`}
                    style={{ color: isOn ? getColorOn() : getColorOff() }}
                >
                    {label}
                </div>
                <PanelStackMeter
                    segments={bar.segments}
                    gapFraction={bar.gapFraction}
                    isOn={isOn}
                    colorOn={getColorOn()}
                    label={label}
                    onSelf={() => panels.toggle(id)}
                    onMoveTo={(index) => {
                        panels.moveTo(id, index);
                        // Placing an off panel is how you bring it back, so the
                        // one click does both.
                        if (!isOn) {
                            panels.toggle(id);
                        }
                    }}
                />
            </div>
        );
    };

    const [colorPickerState, setColorPickerState] = useState<{
        isOpen: boolean;
        position: { x: number; y: number };
        currentNote: keyof ColorScheme;
        isMajor: boolean;
    }>({
        isOpen: false,
        position: { x: 0, y: 0 },
        currentNote: 'R',
        isMajor: true
    });

    // Add this to your existing state
    const [customPalette, setCustomPalette] = useState<ColorScheme>(colorSchemes.softMajMinColors);

    // Add this function to handle color box clicks
    // const handleColorBoxClick = (
    //     event: React.MouseEvent,
    //     note: keyof ColorScheme,
    //     isMajor: boolean
    // ) => {
    //     event.stopPropagation();
    //     const rect = event.currentTarget.getBoundingClientRect();
    //     setColorPickerState({
    //         isOpen: true,
    //         position: {
    //             x: rect.left,
    //             y: rect.bottom + window.scrollY + 10
    //         },
    //         currentNote: note,
    //         isMajor
    //     });
    // };

    // Add this function to handle color changes
    const handleColorChange = (newColor: string) => {
        const { currentNote, isMajor } = colorPickerState;
        const updatedPalette = {
            ...customPalette,
            [currentNote]: isMajor
                ? [newColor, customPalette[currentNote][1]]
                : [customPalette[currentNote][0], newColor]
        };
        setCustomPalette(updatedPalette);

        // Update the context's customColors1 when changes are made
        if (selectedPalette === 'customColors1') {
            setColorScheme(updatedPalette);
        }

        // setCustomPalette(prev => ({
        //     ...prev,
        //     [currentNote]: isMajor
        //         ? [newColor, prev[currentNote][1]]
        //         : [prev[currentNote][0], newColor]
        // }));
    };

    const openPopup = () => {
        setIsOpen(true);
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
    };

    const closePopup = () => {
        setIsOpen(false);
        setIsPaletteSelectOpen(false);
        setColorPickerState(prev => ({ ...prev, isOpen: false }));
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '100%';
    };

    const handleColorSchemeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedScheme = event.target.value;
        setSelectedPalette(selectedScheme);

        // If selecting custom palette, use the local customPalette state
        if (selectedScheme === 'customColors1') {
            setColorScheme(customPalette);
        } else {
            const newColorScheme = colorSchemes[selectedScheme as keyof typeof colorSchemes];
            if (typeof newColorScheme === 'object' && newColorScheme !== null && 'R' in newColorScheme) {
                setColorScheme(newColorScheme);
            }
        }

        // const newColorScheme = colorSchemes[selectedScheme as keyof typeof colorSchemes];
        // if (typeof newColorScheme === 'object' && newColorScheme !== null && 'R' in newColorScheme) {
        //     setColorScheme(newColorScheme);
        // }
    };

    const ColorPalettePopup = () => (
        <div style={{
            position: 'absolute',
            top: '0dvw',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(135, 135, 135, 0.0)',
            zIndex: 1002,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <div style={{
                backgroundColor: `${settings.appTheme === "light" ? "rgba(45, 45, 45, 0.95)" : "rgba(23, 23, 23, 0.95)"}`,
                padding: '1.5dvw',
                borderRadius: '0.7dvw',
                border: 'groove',
                borderColor: '#555',
                maxWidth: '47dvw',
                maxHeight: '80vh',
                overflowY: 'auto'
            }}>
                <div style={{
                    marginBottom: '1dvw',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{ fontSize: '1.2dvw', color: '#ddd' }}>Select Color Palette</span>
                    <span
                        onClick={() => {
                            setIsPaletteSelectOpen(false);
                            setColorPickerState(prev => ({ ...prev, isOpen: false }));
                        }}
                        style={{
                            cursor: 'pointer',
                            padding: '0.3dvw 0.6dvw',
                            borderRadius: '0.3dvw',
                            backgroundColor: `${settings.appTheme === "light" ? "rgba(31, 31, 31, 0.95)" : "rgba(61, 61, 61, 0.95)"}`
                        }}
                    >
                        ✕
                    </span>
                </div>
                <div style={{
                    backgroundColor: '#222',
                    borderRadius: '0.3dvw',
                    padding: '0.5dvw'
                }}>
                    {/* Scale Degrees Header */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '0.8dvw',
                        fontFamily: 'Arial Unicode MS',
                        color: '#999',
                        marginLeft: '10.1dvw',
                        marginBottom: '0.4dvw',
                        paddingLeft: '0.05dvw'
                    }}>
                        {/* Major/Minor Labels */}
                        <div style={{
                            display: 'flex',
                            marginBottom: '0.2dvw'
                        }}>
                            <div style={{
                                width: 'calc(12dvw)', // 12 boxes * 1.1dvw per box
                                textAlign: 'center',
                                fontSize: '0.7dvw',
                                color: '#888'
                            }}>
                                Major
                            </div>
                            <div style={{ width: '0.4dvw' }} />
                            <div style={{
                                width: 'calc(12dvw)', // 12 boxes * 1.1dvw per box
                                textAlign: 'center',
                                fontSize: '0.7dvw',
                                color: '#888'
                            }}>
                                Minor
                            </div>
                        </div>

                        {/* Scale Degrees */}
                        <div style={{
                            display: 'flex',
                            flex: '1',
                            justifyContent: 'flex-start',
                            gap: '0dvw'
                        }}>
                            {majorNotes.map((note) => (
                                <div
                                    key={`major-${note}`}
                                    style={{
                                        width: '1dvw',
                                        height: '1.0dvw',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center'
                                    }}
                                >
                                    {note}
                                </div>
                            ))}
                            <div style={{ width: '0.4dvw' }} />
                            {minorNotes.map((note) => (
                                <div
                                    key={`minor-${note}`}
                                    style={{
                                        width: '1dvw',
                                        height: '1.0dvw',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center'
                                    }}
                                >
                                    {note}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Color Palette Options */}
                    {colorPaletteOptions.map(option => (
                        <div
                            key={option.value}
                            onClick={() => {
                                handleColorSchemeChange({ target: { value: option.value } } as React.ChangeEvent<HTMLSelectElement>);
                                setIsPaletteSelectOpen(false);
                                setColorPickerState(prev => ({ ...prev, isOpen: false }));
                            }}
                            onMouseEnter={() => setHoveredOption(option.value)}
                            onMouseLeave={() => setHoveredOption(null)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0.2dvw',
                                cursor: 'pointer',
                                backgroundColor: selectedPalette === option.value ? '#444' : 'transparent',
                                borderRadius: '0.2dvw',
                                marginBottom: '0.2dvw'
                            }}
                        >
                            <span style={{
                                flex: '0 0 auto',
                                width: '10dvw',
                                color: '#ddd',
                                fontSize: '0.85dvw'
                            }}>
                                {option.label}
                            </span>
                            <div style={{
                                display: 'flex',
                                gap: '0dvw',
                                flex: '1',
                                justifyContent: 'flex-start'
                            }}>
                                {option.value === 'customColors1' ? (
                                    <>
                                        {/* Major colors */}
                                        {Object.entries(customPalette).map(([note, colors], i) => (
                                            <React.Fragment key={note}>
                                                <div
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        console.log('Color box clicked:', {
                                                            rect,
                                                            position: {
                                                                x: rect.left + window.scrollX,
                                                                y: rect.bottom + window.scrollY
                                                            },
                                                            note,
                                                            isMajor: true
                                                        });
                                                        setColorPickerState({
                                                            isOpen: true,
                                                            position: {
                                                                x: rect.left + window.scrollX,
                                                                y: rect.bottom + window.scrollY
                                                            },
                                                            currentNote: note as keyof ColorScheme,
                                                            isMajor: true
                                                        });
                                                    }}
                                                    style={{
                                                        width: '1dvw',
                                                        height: '1.0dvw',
                                                        backgroundColor: colors[0],
                                                        borderRadius: '0.2dvw',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                {i === Object.entries(customPalette).length - 1 && <div style={{ width: '0.4dvw' }} />}
                                            </React.Fragment>
                                        ))}
                                        {/* Minor colors */}
                                        {Object.entries(customPalette).map(([note, colors]) => (
                                            <div
                                                key={`minor-${note}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setColorPickerState({
                                                        isOpen: true,
                                                        position: {
                                                            x: rect.left + window.scrollX,
                                                            y: rect.bottom + window.scrollY
                                                        },
                                                        currentNote: note as keyof ColorScheme,
                                                        isMajor: false
                                                    });
                                                }}
                                                style={{
                                                    width: '1dvw',
                                                    height: '1.0dvw',
                                                    backgroundColor: colors[1],
                                                    borderRadius: '0.2dvw',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        ))}
                                    </>
                                ) : (
                                    colorSchemes[option.value] && (
                                        <>
                                            {/* Display non-editable colors for other palettes */}
                                            {Object.values(colorSchemes[option.value] as Record<string, string[]>).map((colors: string[], i) => (
                                                <div
                                                    key={`major-${i}`}
                                                    style={{
                                                        width: '1dvw',
                                                        height: '1.0dvw',
                                                        backgroundColor: colors[0],
                                                        borderRadius: '0.2dvw'
                                                    }}
                                                />
                                            ))}
                                            <div style={{ width: '0.4dvw' }} />
                                            {Object.values(colorSchemes[option.value] as Record<string, string[]>).map((colors: string[], i) => (
                                                <div
                                                    key={`minor-${i}`}
                                                    style={{
                                                        width: '1dvw',
                                                        height: '1.0dvw',
                                                        backgroundColor: colors[1],
                                                        borderRadius: '0.2dvw'
                                                    }}
                                                />
                                            ))}
                                        </>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <span data-actionable="true" onClick={openPopup} title='Settings'
                style={{
                    border: 'none',
                    top: '-0.1dvw',
                    left: '94.3dvw',
                    cursor: 'pointer',
                    borderRadius: '0dvw',
                    // borderColor: 'black',
                    // backgroundColor: 'black',
                    // color: 'black',
                    fontSize: '0.2dvw',
                    width: '1dvw',
                    padding: '0.3dvw',
                    margin: '0.2dvw',
                    position: 'absolute',
                    zIndex: 1001,
                    display: 'inline-block',
                    pointerEvents: 'auto'
                }}>
                {'\u00a0'}
                <img
                    src="/images/settingsicon-500.png"
                    alt="Settings"
                    style={{
                        width: '1.6dvw',
                        verticalAlign: '-0.1dvw',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        filter: settings.appTheme === "light" ? 'invert(100%)' : 'invert(0)'
                    }}
                />
                {'\u00a0'}
            </span>

            {isOpen && (
                <div ref={contentRef} data-actionable="true" style={{
                    position: 'absolute',
                    top: '3dvw',
                    left: '30%',
                    right: '30%',
                    bottom: '3dvw',
                    backgroundColor: `${settings.appTheme === "light" ? "rgba(70, 70, 70, 0.8)" : "rgba(40, 40, 40, 0.7)"}`,
                    padding: '2dvw',
                    color: '#fff',
                    border: `solid 0.05dvw rgba(150, 150, 150, 0.99)`,
                    borderRadius: '0.8dvw',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    overflowY: 'auto',
                    zIndex: 1000,
                    maxHeight: '52dvw',
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'left',
                        alignItems: 'left',
                        marginBottom: '1.5dvw'
                    }}>

                        <span className='title-text' style={{ fontSize: '1.8dvw', fontWeight: '700' }}>Harmona </span>&nbsp;<span></span>
                        <span className='title-text' style={{ fontSize: '0.8dvw' }}> by Sentisonics</span>{'\u00a0'}
                        {'\u00a0'}
                        {<img src="/images/settingsicon-500.png" alt="" style={{ width: '3dvw', verticalAlign: '-0.4dvw' }} />}
                        {'\u00a0'}
                        <span style={{ fontSize: '1.7dvw', fontWeight: '700', color: 'rgb(220, 220, 220)' }}>{'\u00a0'} | Settings</span>
                        <span>
                            <button
                                onClick={() => {
                                    const nextTheme = settings.appTheme === 'dark' ? 'light' :
                                        settings.appTheme === 'light' ? 'warm' : 'dark';
                                    onSettingChange('appTheme', nextTheme);
                                }}
                                style={{
                                    background: settings.appTheme === 'dark' ? '#000000' :
                                        settings.appTheme === 'light' ? '#ffffff' : 'rgb(217, 178, 110)',
                                    color: settings.appTheme === 'dark' ? '#ffffff' : '#000000',
                                    border: `1px solid ${settings.appTheme === 'dark' ? '#444' :
                                        settings.appTheme === 'light' ? '#ccc' : 'rgb(219, 193, 0)'}`,
                                    borderRadius: '0.4dvw',
                                    fontSize: '0.9dvw',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    width: '3.5dvw',
                                    height: '2.1dvw',
                                    boxShadow: 'none',
                                    padding: '0',
                                    margin: '0.1dvw 1.6dvw',
                                    textAlign: 'center',
                                    textTransform: 'capitalize',
                                    transition: 'none',
                                    position: 'static',
                                    display: 'inline-block'
                                }}
                            >
                                {settings.appTheme}
                            </button>
                        </span>
                    </div>

                    {/* Two-column layout container */}
                    <div style={{ display: 'flex', gap: '1dvw', marginBottom: '0dvw' }}>
                        {/* Left Column */}
                        <div style={{ marginBottom: '0dvw', textAlign: 'left' }}>
                            <div className={`settings-panel-box ${settings.appTheme}`} style={{ outlineColor: '#ddd' }} >
                                <div>Key-Center
                                    <ToggleSwitch
                                        isChecked={settings.showNashvilleNumber}
                                        onChange={(checked) => onSettingChange('showNashvilleNumber', checked)}
                                        labelOff="Roman Numerals"
                                        labelOn="Nashville Numbers"
                                        appTheme={settings.appTheme}
                                    />
                                    <ToggleSwitch
                                        isChecked={settings.showRelativeChordScale}
                                        onChange={(checked) => onSettingChange('showRelativeChordScale', checked)}
                                        labelOff="Show Chord Tones"
                                        labelOn="Show Chord Scale"
                                        appTheme={settings.appTheme}
                                    />
                                    <ToggleSwitch
                                        isChecked={settings.preferModeBorrowing}
                                        onChange={(checked) => onSettingChange('preferModeBorrowing', checked)}
                                        labelOff="Prefer Key Mode"
                                        labelOn="Prefer Mode Borrowing"
                                        appTheme={settings.appTheme}
                                    /></div>
                            </div>
                            <div
                                className={`settings-panel-box ${settings.appTheme}`}
                                style={panelBoxGlow('chordSymbol')}
                            >
                                {panelHeader('chordSymbol', 'Chord Symbol')}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <ToggleSwitch
                                        isChecked={settings.preferNotationLegible}
                                        onChange={(checked) => onSettingChange('preferNotationLegible', checked)}
                                        labelOff="FEEL (Root Perception)"
                                        labelOn="READ (Legibility)"
                                        appTheme={settings.appTheme}
                                    />
                                    <ToggleSwitch
                                        isChecked={settings.showChordQualityColors}
                                        onChange={(checked) => onSettingChange('showChordQualityColors', checked)}
                                        labelOff="Hide Chord Quality Colors"
                                        labelOn="Show Chord Quality Colors"
                                        appTheme={settings.appTheme}
                                    />
                                </div>
                            </div>
                            <div
                                className={`settings-panel-box ${settings.appTheme}`}
                                style={{
                                    ...panelBoxGlow('staff')
                                }}
                            >
                                {panelHeader('staff', 'Staff')}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <ToggleSwitch
                                        isChecked={settings.preferWhiteStaffPaper}
                                        onChange={(checked) => onSettingChange('preferWhiteStaffPaper', checked)}
                                        labelOff="Black Staff Paper"
                                        labelOn="White Staff Paper"
                                        appTheme={settings.appTheme}
                                    />
                                </div>
                            </div>

                            {/* The chart opens itself when it gains chords, but it is a panel like
                                any other: for now,this is how you put an empty one on screen. */}
                            <div
                                className={`settings-panel-box ${settings.appTheme}`}
                                style={{
                                    ...panelBoxGlow('chordChart')
                                }}
                            >
                                {panelHeader('chordChart', 'Chord Chart')}
                                {/* Shows the key center lines and lets the selected chord's key
                                    center lock the app's view. The chart keeps its key center data
                                    up to date through every edit either way. */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <ToggleSwitch
                                        isChecked={settings.showKeyCenterRegions}
                                        onChange={(checked) => onSettingChange('showKeyCenterRegions', checked)}
                                        labelOff="Hide Key-Center Regions"
                                        labelOn="Use Key-Center Regions"
                                        appTheme={settings.appTheme}
                                    />
                                </div>
                                {/* Off, loading a MIDI file asks whether to make a chart from it */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <ToggleSwitch
                                        isChecked={settings.chartMidiOnLoad}
                                        onChange={(checked) => onSettingChange('chartMidiOnLoad', checked)}
                                        labelOff="Ask to Chart MIDI Files"
                                        labelOn="Chart MIDI Files on Load"
                                        appTheme={settings.appTheme}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div style={{ flex: 1, textAlign: 'left' }}>
                            <div className={`settings-panel-box ${settings.appTheme}`} style={{ outlineColor: '#ddd' }} >
                                <div>Piano Keyboard
                                    <ToggleSwitch
                                        isChecked={settings.showScaleDegrees}
                                        onChange={(checked) => onSettingChange('showScaleDegrees', checked)}
                                        labelOff="Hide Scale Degrees"
                                        labelOn="Show Scale Degrees"
                                        appTheme={settings.appTheme}
                                    />
                                    <ToggleSwitch
                                        isChecked={settings.showOvertones}
                                        onChange={(checked) => onSettingChange('showOvertones', checked)}
                                        labelOff="Hide Overtones"
                                        labelOn="Show Overtones"
                                        appTheme={settings.appTheme}
                                    /></div>
                                    
                                    {/* <ToggleSwitch
                                        isChecked={settings.showMidiPlayer}
                                        onChange={(checked) => onSettingChange('showMidiPlayer', checked)}
                                        labelOff="Hide MIDI Player"
                                        labelOn="Show MIDI Player"
                                        appTheme={settings.appTheme} */}

                            </div>
                            <div
                                className={`settings-panel-box ${settings.appTheme}`}
                                style={{
                                    ...panelBoxGlow('chordCompass')
                                }}
                            >
                                {panelHeader('chordCompass', 'Chord Compass')}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <ToggleSwitch
                                        isChecked={settings.showIntervalLabels}
                                        onChange={(checked) => onSettingChange('showIntervalLabels', checked)}
                                        labelOff="Scale-Degree Labels"
                                        labelOn="Interval Labels"
                                        appTheme={settings.appTheme}
                                    />
                                    <ToggleSwitch
                                        isChecked={settings.showPieSentiments}
                                        onChange={(checked) => onSettingChange('showPieSentiments', checked)}
                                        labelOff="Hide Sentiments"
                                        labelOn="Show Sentiments"
                                        appTheme={settings.appTheme}
                                    />
                                    <ToggleSwitch
                                        isChecked={settings.anchorToChordRoot}
                                        onChange={(checked) => onSettingChange('anchorToChordRoot', checked)}
                                        labelOff="Key-Center Anchor"
                                        labelOn="Chord Root Anchor"
                                        appTheme={settings.appTheme}
                                    />
                                </div>
                            </div>

                            <div
                                className={`settings-panel-box ${settings.appTheme}`}
                                style={{
                                    ...panelBoxGlow('luxTonnetz')
                                }}
                            >
                                {panelHeader('luxTonnetz', 'Lux Tonnetz')}
                            </div>

                            <div
                                className={`settings-panel-box ${settings.appTheme}`}
                                style={{
                                    ...panelBoxGlow('sentiment')
                                }}
                            >
                                {panelHeader('sentiment', 'Sentiment')}
                                <div onClick={(e) => e.stopPropagation()}>
                                    {/* <ToggleSwitch // Possible to implement in future
                                        isChecked={settings.showSelectedSentiments}
                                        onChange={(checked) => onSettingChange('showSelectedSentiments', checked)}
                                        labelOn="Show Selected Sentiments"
                                        labelOff="Show Sorted Sentiments"
                                        appTheme={settings.appTheme}
                                    /> */}
                                    <ToggleSwitch
                                        isChecked={settings.showRootFifthSentiment}
                                        onChange={(checked) => onSettingChange('showRootFifthSentiment', checked)}
                                        labelOff="Hide Root & 5th Sentiment"
                                        labelOn="Show Root & 5th Sentiment"
                                        appTheme={settings.appTheme}
                                    />
                                    <Slider
                                        value={settings.secondaryChordThreshold}
                                        onChange={(value) => onSettingChange('secondaryChordThreshold', value)}
                                        min={10}
                                        max={50}
                                        label="Secondary Chord Threshold"
                                        appTheme={settings.appTheme}
                                    />
                                </div>
                                <span
                                    onClick={(e) => { e.stopPropagation(); onOpenSentimentCustomizer(); }}
                                    style={{
                                        fontSize: '0.9dvw',
                                        fontFamily: 'Trebuchet MS',
                                        color: 'rgb(69, 207, 207)',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        margin: '0.5dvw 0.5dvw 0.5dvw 0.5dvw',
                                        backgroundColor: 'rgba(26, 26, 26, 0.35)',
                                        padding: '0.2dvw 0.5dvw',
                                        borderRadius: '0.2dvw',
                                        border: '0.1dvw solid rgb(69, 207, 207)',
                                        display: 'inline-block'
                                    }}
                                >
                                    Customize Sentiments [+]
                                </span>
                            </div>

                        </div>
                    </div>

                    <div style={{ marginBottom: '2dvw', textAlign: 'left' }}>
                        <span>Color Palette</span>
                        <div style={{
                            backgroundColor: `${settings.appTheme === "light" ? "rgba(114, 114, 114, 0.8)" : "rgba(23, 23, 23, 0.8)"}`,
                            borderRadius: '0.3dvw',
                            padding: '0.5dvw',
                            marginBottom: '1.5dvw',
                            marginTop: '0.5dvw'
                        }}>
                            {/* Show only the selected palette */}
                            {colorPaletteOptions.filter(option => option.value === selectedPalette).map(option => (
                                <div
                                    key={option.value}
                                    onClick={() => setIsPaletteSelectOpen(true)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0.2dvw',
                                        cursor: 'pointer',
                                        backgroundColor: '#444',
                                        borderRadius: '0.2dvw',
                                        marginBottom: '0.2dvw'
                                    }}
                                >
                                    <span style={{
                                        flex: '0 0 auto',
                                        width: '10dvw',
                                        color: '#ddd',
                                        fontSize: '0.85dvw'
                                    }}>
                                        {option.label}
                                    </span>
                                    <div style={{
                                        display: 'flex',
                                        gap: '0dvw',
                                        flex: '1',
                                        justifyContent: 'flex-start'
                                    }}>
                                        {colorSchemes[option.value] && (
                                            <>
                                                {Object.values(colorSchemes[option.value] as Record<string, string[]>).map((colors: string[], i) => (
                                                    <div
                                                        key={`major-${i}`}
                                                        style={{
                                                            width: '1dvw',
                                                            height: '1dvw',
                                                            backgroundColor: colors[0],
                                                            borderRadius: '0.2dvw'
                                                        }}
                                                    />
                                                ))}
                                                <div style={{ width: '0.4dvw' }} />
                                                {Object.values(colorSchemes[option.value] as Record<string, string[]>).map((colors: string[], i) => (
                                                    <div
                                                        key={`minor-${i}`}
                                                        style={{
                                                            width: '1dvw',
                                                            height: '1dvw',
                                                            backgroundColor: colors[1],
                                                            borderRadius: '0.2dvw'
                                                        }}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {isPaletteSelectOpen && <ColorPalettePopup />}

                    {isOpen && (
                        <div style={{
                            textAlign: 'left',
                            marginTop: '1.5dvw'
                        }}>
                            <span onClick={closePopup} title='Close Settings' style={{
                                textAlign: 'center',
                                border: 'groove',
                                verticalAlign: '-0.0dvw',
                                cursor: 'pointer',
                                borderRadius: '0.4dvw',
                                padding: '0.1dvw',
                                backgroundColor: 'black',
                                color: 'rgb(220,220,220)',
                                fontSize: '1dvw'
                            }}>
                                {'\u00a0Close\u00a0\u2716'}
                            </span>
                        </div>
                    )}

                    {colorPickerState.isOpen && (
                        <ColorPickerPopup
                            currentColor={customPalette[colorPickerState.currentNote][colorPickerState.isMajor ? 0 : 1]}
                            softMajMinColor={colorSchemes.softMajMinColors[colorPickerState.currentNote][colorPickerState.isMajor ? 0 : 1]}
                            onColorChange={handleColorChange}
                            onClose={() => setColorPickerState(prev => ({ ...prev, isOpen: false }))}
                            position={colorPickerState.position}
                            currentNote={colorPickerState.currentNote}
                            isMajor={colorPickerState.isMajor}
                            customPalette={customPalette}
                        />
                    )}
                </div>
            )}

            {isOpen && (
                <div data-actionable="true" onClick={closePopup} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    zIndex: 999
                }} />
            )}
        </>
    );
};

export default Settings; 